import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DropdownField, Input, Form} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import {Breadcrumb, Row, Col} from 'react-bootstrap';
import * as Yup from 'yup';
import {SYSTEM_OPERATORS} from '@app/utils/constants';
import {sagaActions} from './redux/saga';
import {resetFormSubmitSuccess} from './redux/index';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const ManageSourceEquations = () => {
  const {categoryId, sourceId} = useParams();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const inputFieldsData = useSelector(
    (state) => state.calcEngine.sourceInputFields.data
  );

  const selectedSource = useSelector(
    (state) => state.calcEngine.sourceInputFields.source
  );

  const isLoading = useSelector(
    (state) => state.calcEngine.sourceInputFields.loading
  );

  const formSubmitSuccess = useSelector(
    (state) => state.calcEngine.formSubmitSuccess
  );

  const handleGotoSources = useCallback(() => {
    history.replace(`/calc-engine/${categoryId}/sources`);
  }, [categoryId]);

  useEffect(() => {
    if (formSubmitSuccess) {
      dispatch(resetFormSubmitSuccess(false));
      handleGotoSources();
    }
  }, [formSubmitSuccess]);

  useEffect(() => {
    if (
      inputFieldsData.length === 0 ||
      (inputFieldsData.length &&
        inputFieldsData[0].sourceId !== Number(sourceId))
    ) {
      dispatch({
        type: sagaActions.FETCH_INPUT_TABLE_FIELDS,
        payload: {
          sourceId,
          values: {
            sortOn: 'fieldLevel',
            sortDir: 'ASC',
          },
        },
      });
    }
  }, [inputFieldsData]);

  const standardFields = useMemo(() => {
    const lStandardFields = inputFieldsData
      .filter((field) => field.calculationField === 'Standard-Unit')
      .map((field) => ({value: field.id, label: field.fieldName}));
    return lStandardFields;
  }, [inputFieldsData]);

  const {mainEquation, subEquation} = useMemo(() => {
    const lMainEquation = {
      mainEqInput: '',
      mainEqOperator: '',
      mainEqStandardUnit: '',
    };
    const lSubEquation = {
      subEqInput: '',
      subEqOperator: '',
      subEqStandardUnit: '',
    };
    if (selectedSource) {
      const [mainEqInput, mainEqOperator, mainEqStandardUnit] =
        selectedSource.mainEquation
          ? selectedSource.mainEquation.split('.')
          : ['', '', ''];
      const [subEqInput, subEqOperator, subEqStandardUnit] =
        selectedSource.subEquation
          ? selectedSource.subEquation.split('.')
          : ['', '', ''];
      lMainEquation.mainEqInput = mainEqInput;
      lMainEquation.mainEqOperator =
        SYSTEM_OPERATORS.find((op) => op.value === mainEqOperator) || '';
      lMainEquation.mainEqStandardUnit =
        standardFields.find((sf) => sf.value === Number(mainEqStandardUnit)) ||
        '';
      lSubEquation.subEqInput = subEqInput;
      lSubEquation.subEqOperator = subEqOperator
        ? SYSTEM_OPERATORS.find((op) => op.value === subEqOperator) || ''
        : '';
      lSubEquation.subEqStandardUnit = subEqStandardUnit
        ? standardFields.find((sf) => sf.value === Number(subEqStandardUnit)) ||
          ''
        : '';
    }
    return {mainEquation: lMainEquation, subEquation: lSubEquation};
  }, [inputFieldsData, selectedSource, standardFields]);

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.UPDATE_CATEGORY_SOURCE,
      payload: {
        categoryId,
        sourceId,
        values: {
          mainEquation: `${values.mainEqInput}.${values.mainEqOperator.value}.${values.mainEqStandardUnit.value}`,
          subEquation: `${values.subEqInput}.${
            values.subEqOperator ? values.subEqOperator.value : ''
          }.${values.subEqStandardUnit ? values.subEqStandardUnit.value : ''}`,
        },
      },
    });
  };
  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/'}}>
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/calc-engine'}}>
            Source Categories
          </Breadcrumb.Item>
          <Breadcrumb.Item
            linkAs={Link}
            linkProps={{to: `/calc-engine/${categoryId}/sources`}}
          >
            Emission Sources
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Equations</Breadcrumb.Item>
        </Breadcrumb>
        <StyledH3>{t('owner.calcEngine.pageHeading')}</StyledH3>
        <StyledHeadRow>
          <div className="heading">
            {t('owner.calcEngine.equations.pageHeading')}
            {selectedSource ? ` for ${selectedSource.name}` : ''}
          </div>
        </StyledHeadRow>
        <div className="card mt-1">
          <div className="card-body">
            <Form
              initialValues={{
                ...mainEquation,
                ...subEquation,
              }}
              validationSchema={Yup.object({
                mainEqInput: Yup.string().required(
                  t('owner.calcEngine.equations.required.mainEqInput')
                ),
                //   .test({
                //     name: 'is-valid-input',
                //     message: t(
                //       'reporter.entity.required.notSpecialCharAllowed'
                //     ),
                //     test: (value) => {
                //       return value && /^[a-z0-9]+$/i.test(value) ? true : false;
                //     },
                //   }),
                mainEqOperator: Yup.object().required(
                  t('owner.calcEngine.equations.required.mainEqOperator')
                ),
                mainEqStandardUnit: Yup.object().required(
                  t('owner.calcEngine.equations.required.mainEqStandardUnit')
                ),
                subEqInput: Yup.string().required(
                  t('owner.calcEngine.equations.required.subEqInput')
                ),
                //   .test({
                //     name: 'is-valid-input',
                //     message: t(
                //       'reporter.entity.required.notSpecialCharAllowed'
                //     ),
                //     test: (value) => {
                //       return value && /^[a-z0-9]+$/i.test(value) ? true : false;
                //     },
                //   }),
                // subEqOperator: Yup.string().required(
                //   t('owner.calcEngine.equations.required.subEqOperator')
                // ),
                // subEqStandardUnit: Yup.string().required(
                //   t('owner.calcEngine.equations.required.subEqStandardUnit')
                // ),
              })}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
              enableReinitialize
            >
              <Row>
                <Col>
                  <span>Main Equation</span>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    type="text"
                    name="mainEqInput"
                    id="mainEqInput"
                    label="User Input"
                    placeholder=""
                    required
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="mainEqOperator"
                    name="mainEqOperator"
                    label="Operator"
                    options={SYSTEM_OPERATORS}
                    placeholder=""
                    menuPortalTarget="parent"
                    required
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="mainEqStandardUnit"
                    name="mainEqStandardUnit"
                    label="Standard Unit Field"
                    options={standardFields}
                    placeholder=""
                    menuPortalTarget="parent"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col>
                  <span>Sub Equation</span>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Input
                    type="text"
                    name="subEqInput"
                    id="subEqInput"
                    label="User Input"
                    placeholder=""
                    required
                    description={`To ignore sub equation type "none" in the field`}
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="subEqOperator"
                    name="subEqOperator"
                    label="Operator"
                    options={SYSTEM_OPERATORS}
                    placeholder=""
                    menuPortalTarget="parent"
                  />
                </Col>
                <Col>
                  <DropdownField
                    id="subEqStandardUnit"
                    name="subEqStandardUnit"
                    label="Standard Unit Field"
                    options={standardFields}
                    placeholder=""
                    menuPortalTarget="parent"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <GreenButton type="submit" className="mr-1">
                    {t(`owner.calcEngine.equations.updateBtnLabel`)}
                  </GreenButton>
                  <WhiteButton
                    type="button"
                    className="ml-1"
                    onClick={handleGotoSources}
                  >
                    {t('owner.calcEngine.equations.cancelBtnLabel')}
                  </WhiteButton>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageSourceEquations;
